import React, { useState } from "react";
import { bool, func, string, object } from "prop-types";
import Cropper from "react-easy-crop";

import { Icon } from "../../atoms/icon";
import "./image-cropper-modal.m.css";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    if (url && (url.startsWith("http://") || url.startsWith("https://"))) {
      image.setAttribute("crossOrigin", "anonymous");
    }
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop, originalType) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, originalType || "image/jpeg", 0.95);
  });
};

export const ImageCropperModal = ({
  isOpen,
  imageSrc,
  originalFile,
  onClose,
  onCropComplete,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !imageSrc) return null;

  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleApply = async () => {
    if (!croppedAreaPixels) return;
    setIsLoading(true);
    setError(null);
    try {
      const type = originalFile ? originalFile.type : "image/jpeg";
      const name = originalFile ? originalFile.name.replace(/\s+/g, "-") : "cropped-image.jpg";
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, type);
      if (!croppedBlob) {
        throw new Error("Failed to crop image");
      }
      const croppedFile = new File([croppedBlob], name, { type: croppedBlob.type });
      onCropComplete(croppedFile);
    } catch (err) {
      console.error(err);
      setError("Failed to crop image. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div styleName="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div styleName="modal-content">
        <button styleName="close-button" onClick={onClose} type="button" disabled={isLoading}>
          <Icon name="close" size="xsmall" />
        </button>

        <div styleName="cropper-header">
          <h2 styleName="cropper-title">Crop Profile Picture</h2>
        </div>

        <div styleName="cropper-body">
          {error && <div style={{ color: "red", marginBottom: "10px" }}>{error}</div>}

          <div styleName="cropper-container">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          </div>

          <div styleName="cropper-controls">
            <button
              type="button"
              styleName="zoom-btn"
              onClick={() => setZoom(Math.max(1, zoom - 0.1))}
              disabled={isLoading}
            >
              −
            </button>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              aria-label="Zoom"
              styleName="zoom-slider"
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              disabled={isLoading}
            />
            <button
              type="button"
              styleName="zoom-btn"
              onClick={() => setZoom(Math.min(3, zoom + 0.1))}
              disabled={isLoading}
            >
              +
            </button>
          </div>

          <div styleName="cropper-footer">
            <button
              type="button"
              styleName="crop-cancel-button"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              styleName="crop-apply-button"
              onClick={handleApply}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Apply"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ImageCropperModal.propTypes = {
  isOpen: bool.isRequired,
  imageSrc: string,
  originalFile: object,
  onClose: func.isRequired,
  onCropComplete: func.isRequired,
};
